/**
 * The customer's access routes under `/api/organizations/{organizationId}/access/`: what every member
 * receives, and the organisation's requests for more. Types are in
 * `@upright/common/iam/entitlement/api-types` and `request-api-types`.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EntitlementCustomerAccessSummary,
  EntitlementCustomerPoliciesResponse,
} from '@upright/common/iam/entitlement/api-types';
import {
  CreateAccessRequestRequest,
  CustomerAccessRequest,
  CustomerAccessRequestsResponse,
  EntitlementRequestErrorCode,
} from '@upright/common/iam/entitlement/request-api-types';
import { v4 as uuid } from '@upright/common/utils/uuid';
import { ErrorWithHttpStatus } from '../../common/error-with-http-status';
import { buildRequestAndFetch } from '../../common/fetcher-core';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';
import { getJsonOrUndefined } from '../../common/response';
import { getErrorMessage } from '../../common/types';
import { maybeHandleCustomError } from '../../common/util';

const accessQueryKey = (organizationId: number) => ['organizations', organizationId, 'access'] as const;

export function getCustomerAccessSummaryQuery(organizationId: number) {
  return queryOptions({
    queryKey: [...accessQueryKey(organizationId), 'summary'],
    queryFn: async () =>
      await fetchOrThrow<EntitlementCustomerAccessSummary>(`/organizations/${organizationId}/access/summary`),
  });
}

/** The member policies that apply to the signed-in member: their own, never another member's. */
export function getCustomerOwnPoliciesQuery(organizationId: number) {
  return queryOptions({
    queryKey: [...accessQueryKey(organizationId), 'policies'],
    queryFn: async () =>
      await fetchOrThrow<EntitlementCustomerPoliciesResponse>(`/organizations/${organizationId}/access/policies`),
  });
}

/** A member's own requests, or every request of the organisation to its admins. */
export function getCustomerAccessRequestsQuery(organizationId: number) {
  return queryOptions({
    queryKey: [...accessQueryKey(organizationId), 'requests'],
    queryFn: async () =>
      await fetchOrThrow<CustomerAccessRequestsResponse>(`/organizations/${organizationId}/access/requests`),
  });
}

/** A refused request, with the reason the server gave: an open request for the same target, say. */
export class AccessRequestError extends ErrorWithHttpStatus {
  constructor(
    message: string,
    status: number,
    public code: EntitlementRequestErrorCode | null,
  ) {
    super(message, status);
  }
}

// `fetchOrThrow` keeps only the message; the form needs the code to say which refusal it was.
async function postAccessRequest(
  organizationId: number,
  request: CreateAccessRequestRequest,
): Promise<CustomerAccessRequest> {
  const response = await buildRequestAndFetch(`/organizations/${organizationId}/access/requests`, 'POST', request);
  const body = await getJsonOrUndefined<CustomerAccessRequest & { code?: EntitlementRequestErrorCode }>(response);
  if (response.ok && body) {
    return body;
  }
  await maybeHandleCustomError(body);
  throw new AccessRequestError(
    getErrorMessage(body) ?? `${response.statusText} (${response.status})`,
    response.status,
    body?.code ?? null,
  );
}

/** Asks for more; then the request lists refresh, wherever they are shown. */
export function useCreateAccessRequestMutation(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation<CustomerAccessRequest, AccessRequestError, Omit<CreateAccessRequestRequest, 'requestId'>>({
    mutationFn: async request => await postAccessRequest(organizationId, { ...request, requestId: uuid() }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: getCustomerAccessRequestsQuery(organizationId).queryKey });
    },
  });
}

export function useWithdrawAccessRequestMutation(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation<CustomerAccessRequest, Error, string>({
    mutationFn: async requestId =>
      await fetchOrThrow<CustomerAccessRequest>(
        `/organizations/${organizationId}/access/requests/${requestId}`,
        'DELETE',
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: getCustomerAccessRequestsQuery(organizationId).queryKey });
    },
  });
}
